import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", slug: "", description: "", image: "", position: 0 };

export default function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/categories").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, position: Number(editing.position) || 0 };
      if (editing.id) {
        await api.put(`/admin/categories/${editing.id}`, payload);
        toast.success("Catégorie mise à jour");
      } else {
        await api.post("/admin/categories", payload);
        toast.success("Catégorie créée");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ? Les produits associés resteront visibles dans « Toutes ».`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setEditing((ed) => ({ ...ed, image: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const input = "mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal";

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Catégories</h1>
          <p className="text-sm text-ink/60 mt-1">Les univers affichés sur l'accueil et les filtres de la boutique.</p>
        </div>
        <button data-testid="admin-category-new" onClick={() => setEditing({ ...EMPTY, position: items.length + 1 })} className="flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          <Plus size={16} /> Nouvelle catégorie
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {items.map((c) => (
          <div key={c.id} data-testid={`admin-category-${c.slug}`} className="bg-white rounded-3xl border border-line p-4 md:p-5 flex items-center gap-4">
            <div className="h-14 w-14 shrink-0 rounded-2xl bg-cream-light overflow-hidden border border-line">
              {c.image && <img src={c.image} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-teal-dark">{c.name} <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-mauve">#{c.position}</span></p>
              <p className="text-sm text-ink/70 truncate">{c.description}</p>
            </div>
            <button data-testid={`admin-category-edit-${c.slug}`} onClick={() => setEditing({ ...EMPTY, ...c })} className="p-2 text-teal" aria-label="Modifier"><Pencil size={16} /></button>
            <button data-testid={`admin-category-delete-${c.slug}`} onClick={() => remove(c)} className="p-2 text-[#b04a4a]" aria-label="Supprimer"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-teal-deep/40 backdrop-blur-sm p-4" data-testid="admin-category-modal">
          <form onSubmit={save} data-lenis-prevent className="w-full max-w-xl max-h-[90vh] overflow-y-auto overscroll-contain bg-white rounded-3xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-teal-dark">{editing.id ? "Modifier la catégorie" : "Nouvelle catégorie"}</h2>
              <button type="button" data-testid="admin-category-modal-close" onClick={() => setEditing(null)} aria-label="Fermer"><X size={22} className="text-mauve" /></button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-ink">
                Nom *
                <input required data-testid="admin-category-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={input} />
              </label>
              <label className="block text-sm font-medium text-ink">
                Description courte
                <input data-testid="admin-category-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={input} />
              </label>
              <label className="block text-sm font-medium text-ink">
                Ordre d'affichage
                <input type="number" min="0" data-testid="admin-category-position" value={editing.position} onChange={(e) => setEditing({ ...editing, position: e.target.value })} className={input} />
              </label>
              <div className="text-sm font-medium text-ink">
                Visuel
                <div className="mt-2 flex items-center gap-4">
                  <div className="h-20 w-20 rounded-2xl bg-cream-light border border-line overflow-hidden">
                    {editing.image && <img src={editing.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm cursor-pointer hover:border-teal">
                    {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                    {uploading ? "Envoi…" : "Choisir une image"}
                    <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="admin-category-image-input" />
                  </label>
                </div>
              </div>
            </div>
            <button type="submit" data-testid="admin-category-save" disabled={saving} className="mt-8 w-full rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
